import { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router'
import { Dispatch } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'

import { usePrevious } from '../hooks'
import {
  addNotification as addNotificationAction,
  removeNotification as removeNotificationAction
} from '../notifications/notifications-actions'
import type { NotificationType } from '../notifications/types'
import { killState as killStateAction } from '../rootReducer'
import { WrappedTdsModal } from '../shared/TdsModal'
import type { GlobalState } from '../types'
import { IdentityDisplay } from './IdentityDisplay'
import { IdentityForm } from './IdentityForm'

type IdentityProps = {
  addNotification(arg: NotificationType): void
  removeNotification(arg: string): void
  claim: {
    lob: string
    dateOfLoss: string
    claim: string
  }
  error?: string[]
  notifications: NotificationType[]
  killState(): void
}

const backModalText = (
  <div>
    <p>The information you have entered so far will be lost.</p>
    <p>Press Okay to Continue, or Cancel to remain on this page.</p>
  </div>
)

const IdentityComponent = (props: IdentityProps) => {
  const {
    addNotification,
    removeNotification,
    claim,
    notifications,
    error,
    killState
  } = props
  const navigate = useNavigate()
  const prevProps = usePrevious<IdentityProps>(props)
  const [backModalOpen, setBackModalOpen] = useState<boolean>(false)

  const handleBrowserButtonClick = useCallback(() => {
    setBackModalOpen(true)
  }, [])

  const handleContinue = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault()
    navigate(`${window.travURI}upload`)
  }, [navigate])

  const handleClose = useCallback(() => {
    setBackModalOpen(false)
  }, [])

  const handleOk = useCallback(() => {
    killState()
    navigate(window.travURI)
  }, [killState, navigate])

  // converted from componentDidMount
  useEffect(() => {
    // only want to run once on mount, when prevProps is undefined
    if (prevProps) return
    document.title = 'File Uploads - Contact Details'

    window.addEventListener('popstate', handleBrowserButtonClick)
    window.history.pushState(null, document.title, document.location.pathname)
  }, [
    claim,
    handleBrowserButtonClick,
    prevProps
  ])

  // converted from componentDidUpdate
  useEffect(() => {
    if (error && !prevProps?.error) {
      addNotification({
        name: 'identityError',
        body: error.join(''),
        severity: 'warning',
        title: 'REQUIRED FIELD'
      })
    }

    if (!error && notifications.length > 0) {
      removeNotification('identityError')
    }

  }, [
    error,
    notifications,
    removeNotification,
    addNotification,
    prevProps?.error
  ])

  // converted from componentWillUnmount
  useEffect(() => {
    return () => {
      if (notifications.length > 0) {
        removeNotification('identityError')
      }
    }
  }, [notifications, removeNotification])

  return (
    <IdentityDisplay>
      <IdentityForm
        handleSubmit={handleContinue}
        openWarningModal={() => setBackModalOpen(true)}
      />
      {backModalOpen && (
        <WrappedTdsModal
          modalTitle="Warning"
          size="medium"
          buttonText="Okay"
          onTdsClose={handleClose}
          label="Warning Modal"
          handleOk={handleOk}
          hasCancelButton={true}
        >
          {backModalText}
        </WrappedTdsModal>
      )}
    </IdentityDisplay>
  )
}

const identityFormSelector = (state: GlobalState) => state.form.identityForm

const getError = createSelector(
  identityFormSelector,
  formValues =>
    formValues
    && formValues.submitFailed
    && formValues.syncErrors ? ['Some items below need your attention.'] : undefined
)

const notificationSelector = (state: GlobalState) => state.notifications.notifications

const getNotifications = createSelector(
  notificationSelector,
  notifications => notifications.filter((n: NotificationType) => n.name === 'identityError')
)

const mapStateToProps = (state: GlobalState) => ({
  error: getError(state),
  claim: state.claim.claimDetails,
  notifications: getNotifications(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  killState: () => dispatch(killStateAction()),
  removeNotification: (name: string) => dispatch(removeNotificationAction(name)),
  addNotification: (options: NotificationType) => dispatch(addNotificationAction(options))
})

const Identity = connect(mapStateToProps, mapDispatchToProps)(IdentityComponent)

export {
  Identity,
  IdentityComponent // for testing without HOC
}
